import axios from 'axios';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Skeleton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import React, { useState } from 'react';

import { toast } from 'react-toastify';

import { useAuth } from '@src/contexts/authContext';
import { removeNullish } from '@src/utils';

const url = `${process.env.REACT_APP_API_URL}/v2/admin/scopes`;

export default function EditListingScope({ open, setOpen, fetchData }) {
  const { auth } = useAuth();
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState({
    label: undefined,
    value: undefined,
    help: {
      title: undefined,
      description: undefined,
    },
    sort: undefined,
    is_deleted: false,
  });
  const itemId = React.useMemo(() => open, [open]);

  React.useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        if (open && auth?.idToken) {
          setLoading(true);
          const response = await axios.get(
            `${url}/items/${itemId}?with_deleted=1`,
            {
              headers: {
                Authorization: `Bearer ${auth.idToken}`,
              },
            },
          );

          setData({
            ...(response.data?.data || {}),
          });
        }
      } catch (error) {
        console.error(error);
      }
      setLoading(false);
    };

    fetchDataAsync();
  }, [auth?.idToken, open, itemId]);

  const handlePost = async () => {
    const formData = removeNullish({
      label: data?.label,
      value: data?.value,
      help:
        data?.help?.title || data?.help?.description
          ? {
              title: data?.help?.title,
              description: data?.help?.description,
            }
          : undefined,
      sort: data?.sort,
    });

    if (auth?.idToken && !!Object.values(formData).filter((i) => !!i).length) {
      setLoading(true);
      await axios
        .put(`${url}/items/${itemId}/edit`, formData, {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.idToken}`,
          },
        })
        .then((response) => {
          setData({ ...(response.data?.data ?? data) });
          fetchData();
          toast.success('Item updated successfully', {
            position: 'top-center',
            autoClose: 2000,
          });
        })
        .catch((error) => {
          console.error(error.message);
          const errorMsg =
            typeof error.response?.data?.errors === 'object'
              ? Object.values(error.response.data.errors)[0]?.msg
              : error.message;
          toast.error(`${errorMsg}`, {
            position: 'top-center',
            autoClose: 2000,
          });
        })
        .finally(() => {
          setLoading(false);
          setOpen(null);
        });
    }
  };

  return (
    <Dialog maxWidth='lg' fullwidth open={!!open} onClose={() => setOpen(null)}>
      <DialogTitle>Edit item</DialogTitle>
      <br />

      <DialogContent>
        <Box flex={1} mr={1}>
          {isLoading ? (
            <>
              <Skeleton
                variant='text'
                width={50}
                height={24}
                sx={{ mb: '8px' }}
              />
              <Skeleton
                variant='rectangular'
                width='600px'
                height={56}
                sx={{ borderRadius: '6px' }}
              />
              <Skeleton
                variant='text'
                width={50}
                height={24}
                sx={{ mb: '8px' }}
              />
              <Skeleton
                variant='rectangular'
                width='600px'
                height={56}
                sx={{ borderRadius: '6px' }}
              />
              <Skeleton
                variant='text'
                width={50}
                height={24}
                sx={{ mb: '8px' }}
              />
              <Skeleton
                variant='rectangular'
                width='600px'
                height={56}
                sx={{ borderRadius: '6px' }}
              />
              <Skeleton
                variant='text'
                width={50}
                height={24}
                sx={{ mb: '8px' }}
              />
              <Skeleton
                variant='rectangular'
                width='600px'
                height={56}
                sx={{ borderRadius: '6px' }}
              />
              <Skeleton
                variant='text'
                width={50}
                height={24}
                sx={{ mb: '8px' }}
              />
            </>
          ) : (
            <>
              <Typography fontFamily='Poppins'>Value</Typography>
              <TextField
                fullWidth
                size='medium'
                variant='outlined'
                placeholder='Enter value'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                  },
                  mt: '5px',
                  mb: '10px',
                  width: '600px',
                }}
                value={data?.value ?? null}
                onChange={(e) =>
                  setData({
                    ...data,
                    value: e.target.value,
                  })
                }
              />

              <Typography fontFamily='Poppins'>Label</Typography>
              <TextField
                fullWidth
                size='medium'
                variant='outlined'
                placeholder='Enter label'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                  },
                  mt: '5px',
                  mb: '10px',
                  width: '600px',
                }}
                value={data?.label ?? null}
                onChange={(e) =>
                  setData({
                    ...data,
                    label: e.target.value,
                  })
                }
              />

              <Typography fontFamily='Poppins'>Helper Title</Typography>
              <TextField
                fullWidth
                size='medium'
                variant='outlined'
                placeholder='Enter helper title'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                  },
                  mt: '5px',
                  mb: '10px',
                  width: '600px',
                }}
                value={data?.help?.title ?? null}
                onChange={(e) =>
                  setData({
                    ...data,
                    help:
                      data?.help?.title || data?.help?.description
                        ? {
                            ...data.help,
                            title: e.target.value,
                          }
                        : {
                            title: e.target.value,
                          },
                  })
                }
              />

              <Typography fontFamily='Poppins'>Helper Description</Typography>
              <TextField
                fullWidth
                size='medium'
                variant='outlined'
                placeholder='Enter helper description'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                  },
                  mt: '5px',
                  mb: '10px',
                  width: '600px',
                }}
                value={data?.help?.description ?? null}
                onChange={(e) =>
                  setData({
                    ...data,
                    help:
                      data?.help?.title || data?.help?.description
                        ? {
                            ...data.help,
                            description: e.target.value,
                          }
                        : {
                            description: e.target.value,
                          },
                  })
                }
              />

              <Typography fontFamily='Poppins'>List No</Typography>
              <TextField
                fullWidth
                size='medium'
                variant='outlined'
                placeholder='Enter sort'
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                  },
                  mt: '5px',
                  width: '600px',
                }}
                value={data?.sort ?? null}
                onChange={(e) =>
                  setData({
                    ...data,
                    sort: e.target.value.replace?.(/\D/g, '')
                      ? Number(e.target.value.replace(/\D/g, ''))
                      : undefined,
                  })
                }
              />

              <Box
                sx={{
                  mt: '10px',
                  width: '600px',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={data?.is_deleted}
                      onChange={(e) =>
                        setData({
                          ...data,
                          is_deleted: Boolean(e.target.checked),
                        })
                      }
                    />
                  }
                  label={
                    <Typography fontFamily='Poppins'>
                      Mark as deleted
                    </Typography>
                  }
                />
              </Box>
            </>
          )}

          <br />
          <br />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          sx={{ textTransform: 'none' }}
          onClick={() => {
            setOpen(null);
          }}
          color='secondary'
        >
          Cancel
        </Button>
        <Button
          sx={{ textTransform: 'none' }}
          color='primary'
          onClick={handlePost}
        >
          {isLoading ? <CircularProgress /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
